import { ViewData } from '../../../model/view.data';
import { Model } from '../../../model/model';
import { BusinessUnitVO } from '../../../vo/core/business-unit.vo';
import { VisionVO } from '@wizeoo/akm-compute-lib';
import { FreeDataVO } from '../../../vo/core/freedata.vo';
import { Baseline, BaselineValue, CrossVisionObj } from '@wizeoo/akm-message-lib';
import { FeiKeyDef, WidgetLine, EM_ACTION, FeiVO } from '@wizeoo/akm-compute-lib';
import { AttachmentService } from '../attachment/attachment.service';
import { FreedataValueVO } from '../../../vo/core/freedatavalue.vo';
import { AkmBDD, LocalVision } from '@wizeoo/akm-worker-lib';


/** mémorisation d'un ordre d'update pour éventuel revert par ctrl-Z */
export interface UpdateRevert {
  updateRequest?: UpdateRequest,
  updates?: UpdateEntry[],
  fdv?: FreedataValueVO,
  argCode?: string,
  value?: any
}

/** données pour demande de création d'update depuis les popUps */
export interface UpdateRequest {
  fei: FeiVO; // fei porteur
  line: WidgetLine; // la widgetLine porteuse, pour identifier le scenario cible
  lines: WidgetLine[]; // lignes manipulées
  operation: EM_ACTION; // action cible
  fkd?: FeiKeyDef; // éventuel champ cible (filter, measure)
  value?: any; // éventuelle valeur cible
  doSplit: boolean; // demande de split sur la valeur lors de la génération des updateEntry
  freedata?: Map<string, FreeDataVO>; // passage multi-FD
}

/**
 * une modification de donnée (unitaire, mais membre d'une collection d'updates)
 * porte une baseline, une baselineValue, ou les 2
 * chaque objet porte son action (CUD)
 */
export interface UpdateEntry {
  fei: FeiVO; // fei porteur
  baseline: Baseline; // la baseline cible
  baselineValue?: BaselineValue; // la baselineValue cible

  field: string; // le champ (la measure#mode ou filterKey) modifié

  // filterKey: boolean; // = false; // c'est une filterKey qui est mise à jour
  oldValue: string; // ancienne valeur
  newValue: string; // nouvelle valeur

  baselineOperation: string; // l'opération sur la baseline
  baselineValueOperation: string; // l'opération sur la baselineValue
  ukey: string; // clé unique pour identifier cet update scen@baselineKey@field

  crossVision?: CrossVisionObj; // un patch pour alimenter du crossVision sur le dataCommit
  sourceTrigger?: string; // lien vers l'appel dexies
}

// pack de modification pour appel remote
export interface CompositeSourcePack  {
  freedata?: Map<string, FreeDataVO>; // update / create / delete sur FDV
  freedataManagement?: boolean; // appel depuis vue de gestion des FREEDATAS. Va mettre à jour le porteur( dataCommit)
  updates?: UpdateEntry[]; // maj sur des Baselines / BLVs
  hasError?: boolean; // erreur détectée sur la synchro
  hasRemoteUpdate?: boolean; // modif sur même périmètre
}


/** service de gestion des FD / FDVs. Synchro cache local et appels par incréments */
export class SyncService {

  protected _model: Model = null;
  constructor(model: Model) {
    this._model = model;
  }
  public get model(): Model {
    return this._model as Model;
  }
  
  /** map des éléments locaux par key */
  private _dataMap: Map<string, LocalVision> = null; // 
  private _dataLoading: boolean = false;
  private _pendingBU: Set<BusinessUnitVO> = new Set();
  private _pendingVision: Set<VisionVO> = new Set();




 /**
 * contrôle de cohérence sur réception de BU / vision.
 * Si minRevNumber > à celui en cache
 * => suppression FD / FDV pour BU
 * => suppression BL / BLV pour vision
 * @returns void
 */
  public checkLocal(bu?: BusinessUnitVO, vision?: VisionVO): void {
    
    if(!this._dataMap) { // construction de la map si non alimentée
      if(!this._dataLoading) { // une seule fois
        this._dataLoading = true;
        AkmBDD.getInstance().getAll(AkmBDD.BDD_VISION_KEY).subscribe(vals => {
          this._dataMap = new Map<string, LocalVision>();
          if(vals?.length > 0) {
              vals.forEach((lv: LocalVision) => {
                this._dataMap.set(lv.key, lv);
              });
              
          }
          this._dataLoading = false;
          [...this._pendingBU.values()].forEach(bu => {
            this.reallyDoCheck(bu);
          });
          [...this._pendingVision.values()].forEach(vis => {
            this.reallyDoCheck(null, vis);
          });
          // this.reallyDoCheck(bu, vision);
          
          // console.log('AkmBDD.getInstance().getAll', this._dataMap);
        });
      }

      if(bu) {
        this._pendingBU.add(bu);
      }
      if(vision) {
        this._pendingVision.add(vision);
      }

    } else {
      this.reallyDoCheck(bu, vision);
    }

    
  }

  /** le cache local est chargé, check */
  private reallyDoCheck(bu?: BusinessUnitVO, vision?: VisionVO): void {
    

    // retour chargement local
    let newLocal: LocalVision = null;
    if (!!bu) {
      newLocal = this.buToLocal(bu);
    } else if (!!vision) {
      newLocal = this.visionToLocal(vision);
    }

    // datamap mal alimentée: pas BU
    const exist: LocalVision = this._dataMap.get(newLocal.key);
    // if(newLocal?.key === "DCMJ|PCO|PCOE24") {
    // console.log('got you', newLocal, exist)
    // }

    // if(bu?.businessUnitCode === 'TEXDECOR') {
    //   console.log('checkLocal', bu, vision);
    // }

    if(!!exist) {
      if (exist.minRevNumber < newLocal.minRevNumber) {
        
        if(newLocal.isBU) {  // on rince les FD
          //console.log('syncService checkLocal clear FD', exist.minRevNumber, newLocal.minRevNumber);
          this.clearFD();
      } else { // on rince les baselines
          // console.log('reset FEI sync.service', exist);
          this.clearFEI();
        }
      }
    }
    this._dataMap.set(newLocal.key, newLocal);
    this.persistLocal(newLocal);
}

  /** suppression du cache des FD */
  public clearFD(): void {
    AkmBDD.getInstance().clear(AkmBDD.BDD_FREEDATA_KEY);
    // AkmBDD.getInstance().clear(AkmBDD.BDD_FREEDATAVALUE_KEY);
    // on rince la matrice de FDV
    this.model.cellMatrixEngine.clearFDV();
        // on rince la matrice de calcul
    this.model.cellMatrixEngine.clear();
  }

  /** suppression des FEIs et baselines */
  public clearFEI(): void {
    AkmBDD.getInstance().clear(AkmBDD.BDD_FEI_KEY);
    AkmBDD.getInstance().clear(AkmBDD.BDD_BASELINE_KEY);

    // on rince la matrice de calcul
    this.model.cellMatrixEngine.clear();
  }

  /** suppression du cache des médias */
  public clearMedia(): void {
    AkmBDD.getInstance().clear(AkmBDD.BDD_MEDIA_KEY);
    AttachmentService.getInstance().clearAll();
  }

  public clearWorkcom(): void {
    AkmBDD.getInstance().clear(AkmBDD.BDD_WORKCOM_KEY);
  }

  public clearMatrix(): void {
    AkmBDD.getInstance().clear(AkmBDD.BDD_MATRIX_KEY);
    AkmBDD.getInstance().clear(AkmBDD.BDD_MATRIX_DATA_KEY);
  }

  public buToLocal(bu: BusinessUnitVO): LocalVision {
    const localVision = {
      key: null, 
      minRevNumber: bu.minRevNumber, 
      domain: bu.domain, 
      businessUnitCode:bu.businessUnitCode, 
      vision: ViewData.wildcardDomain,
      isBU: true,
      nbStep: 0
    }
    this.buildKey(localVision);
    return localVision;
  }

  public visionToLocal(vision: VisionVO): LocalVision {
    const localVision = {
      key: null, 
      minRevNumber: vision.minRevNumber, 
      domain: vision.domain, 
      businessUnitCode:vision.businessUnitCode, 
      vision: vision.code, 
      isBU: false,
    }
    this.buildKey(localVision);
    return localVision;
  }

  /**
   * alimente la clé
   * @param lv 
   */
  private buildKey(lv: LocalVision): void {
    const key: string = [lv.domain, lv.businessUnitCode, lv.vision].join(ViewData.KEY_SEPARATOR);
    lv.key = key;
  }

  /** enregistrement dans le jeu local et la map */
  private persistLocal(lv: LocalVision): void {
    AkmBDD.getInstance().doAdd(AkmBDD.BDD_VISION_KEY, lv.key, lv);
  }

  /**
   * 
   * @param updateRequest 
   * @param fd 
   */
  public addFreedataToRequest(updateRequest: UpdateRequest, fd: FreeDataVO): void {
    if(!updateRequest.freedata) {
      updateRequest.freedata = new Map();
    }
    updateRequest.freedata.set(fd.code, fd);
  }

}
